import React, { useCallback, useEffect, useState } from 'react';

import { Formik } from 'formik';
import { useDebounce } from 'react-use';

import { DialogActions, DialogContent, Typography } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { GroupChannelListQuery } from '@sendbird/chat/groupChannel';
import { type GroupChannel } from '@sendbird/chat/groupChannel';

import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import TextField from 'src/components/ui/TextField';

import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';

import SimpleHorizontalGroupChannelCard from 'src/components/ui/SimpleHorizontalGroupChannelCard';
import { breakpoints } from 'src/constants/breakpoints';
import {
    useSearchAllUsersWithTotalQuery,
    useSendChatNotificationMessageMutation,
} from 'src/hooks/__generated__/queries';
import type { ModalProps } from 'src/hooks/useModalOpenState';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';
import type { SearchAllUsersWithTotalQuery } from 'src/types/__generated__/graphql';
import { ChatNotificationType, type ChatNotificationData, type User } from 'src/types/__generated__/graphql';
import {
    GROUP_CONVERSATION_CUSTOM_TYPE,
    createGroupChannelsQuery,
    createGroupConversation,
    getUserChannels,
} from 'src/utils/sendbirdUtils';
import { styledComponent, styledTag } from 'src/utils/styled';
import CreateOrAddMembersGroupConversationModal from 'src/components/chat/CreateOrAddMembersGroupConversationModal';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        position: relative;
        height: 70%;
    }
    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        .MuiDialog-paper {
            height: 100%;
        }
    }
`;

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding: 1.5rem;
    padding-bottom: 0.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 0 1.5rem;
    border-radius: 0.5rem;
`;

const StyledBoxButtonContainer = styledComponent(Box)`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    padding: 1rem;
    box-shadow: 0 0.5rem 2.5rem rgb(19 46 72 / 16%);
    z-index: 10;
`;

const UserWrapper = styledComponent(Box)<{
    $selected: boolean;
}>`
    > div {
        ${({ $selected }) => $selected && `background-color: ${colours.secondary.O12};`}
    }
`;

const ScrollContainer = styledTag('div')`
    overflow-y: scroll;
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

type SearchAllUsersWithTotalReponse = NonNullable<SearchAllUsersWithTotalQuery['searchAllUsersWithTotal']>;

interface Props extends ModalProps {
    title: string;
    topArea?: React.ReactNode | React.ReactNode[];
    submitLabel: string;
    onSelection: (recipient: User | GroupChannel) => void;
    filterOutUsersWhoAlreadyRecommendedCurrentUser?: boolean;
    renderGroupConversationButton?: boolean;
    showConversations?: boolean;
}

const SearchAllUsersAndGroupsModal: React.FC<Props> = ({
    title,
    topArea,
    submitLabel,
    onSelection,
    open,
    onClose,
    filterOutUsersWhoAlreadyRecommendedCurrentUser = false,
    renderGroupConversationButton,
    showConversations,
}) => {
    const { t } = useTranslation(['common', 'contacts']);
    const defaultLimit = 10;
    const [allUsersDataLoaded, setAllUsersDataLoaded] = useState<boolean>(false);
    const [allGroupChannelsDataLoaded, setAllGroupChannelsDataLoaded] = useState<boolean>(false);
    const [query, setQuery] = useState('');
    const [selectedRecipient, setSelectedRecipient] = useState<User | GroupChannel | undefined>();
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [groupChannels, setGroupChannels] = useState<GroupChannel[]>([]);
    const [groupChannelsQuery, setGroupChannelsQuery] = useState<GroupChannelListQuery>();
    const currentUser = useUser();
    const [sendChatNotificationMessage] = useSendChatNotificationMessageMutation();
    const { props: createOrAddMembersGroupConversationModalProps, open: openCreateOrAddMembersGroupConversationModal } =
        useModalOpenState();
    const { logEventDynamic } = useDynamicLogEvents();

    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    useEffect(() => {
        const searchChannels = async () => {
            const groupQuery = createGroupChannelsQuery({
                search: debouncedQuery,
            });
            if (groupQuery) {
                setGroupChannelsQuery(groupQuery);
                const channels = await getUserChannels(groupQuery);
                setGroupChannels(channels);
                setAllGroupChannelsDataLoaded(!groupQuery.hasNext);
            }
        };
        if (open && showConversations) {
            searchChannels().catch(console.error);
        }
    }, [debouncedQuery, showConversations, open]);

    const {
        data: searchAllUsersWithTotal,
        loading,
        fetchMore,
    } = useSearchAllUsersWithTotalQuery({
        variables: {
            offset: 0,
            limit: defaultLimit,
            search: debouncedQuery.length > 0 ? debouncedQuery : null,
        },
        skip: !open || (showConversations && !allGroupChannelsDataLoaded), // Don't fetch users before all conversations are fetched if showConversations
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.searchAllUsersWithTotal) {
                const allLoaded = data.searchAllUsersWithTotal.results?.length === data.searchAllUsersWithTotal.total;
                setAllUsersDataLoaded(allLoaded);
            }
        },
    });

    // Removed users already in the list because there is a conversation with it
    const filterDuplicateUsersWithGroupChannels = useCallback(
        (users: SearchAllUsersWithTotalReponse['results'], channels: GroupChannel[]) => {
            if (!channels.length || !users?.length) {
                return users ?? [];
            }
            const channels1o1Names = channels
                .filter((channel) => channel.customType !== GROUP_CONVERSATION_CUSTOM_TYPE)
                .map((channel) => {
                    const otherMember = channel.members.find(({ userId }) => userId !== currentUser?.sendbirdUserId);
                    return otherMember?.nickname;
                })
                .filter<string>((name): name is string => Boolean(name));

            return users.filter((user) => !channels1o1Names.some((channelName) => channelName === user?.fullName));
        },
        [currentUser],
    );

    let users = filterDuplicateUsersWithGroupChannels(
        searchAllUsersWithTotal?.searchAllUsersWithTotal?.results ?? [],
        groupChannels,
    );
    if (filterOutUsersWhoAlreadyRecommendedCurrentUser) {
        users = users.filter((user) => !user?.recommendationToMe);
    }

    const handleClose = () => {
        setSelectedRecipient(undefined);
        setQuery('');
        setAllGroupChannelsDataLoaded(false);
        onClose();
    };

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        if (bottom) {
            if (!allUsersDataLoaded && !loading) {
                await fetchMore({
                    variables: {
                        offset: searchAllUsersWithTotal?.searchAllUsersWithTotal?.results?.length,
                    },
                });
            }
            if (!allGroupChannelsDataLoaded && groupChannelsQuery && !groupChannelsQuery.isLoading) {
                const newChannels = await getUserChannels(groupChannelsQuery);
                if (!groupChannelsQuery.hasNext) {
                    setAllGroupChannelsDataLoaded(true);
                }
                setGroupChannels((currentGroupChannels) => [...currentGroupChannels, ...newChannels]);
            }
        }
    };

    const onCreateOrAddMembersGroupConversationSelection = async (
        selectedUsers: User[],
        groupConversationName?: string,
    ) => {
        handleClose();
        const channel = await createGroupConversation(currentUser as User, selectedUsers, groupConversationName);
        if (channel) {
            const chatNotification: ChatNotificationData = {
                notificationType: ChatNotificationType.ChannelCreation,
            };
            await sendChatNotificationMessage({
                variables: {
                    channelUrl: channel.url,
                    notification: chatNotification,
                },
            });
            onSelection(channel);
        }
    };

    const isLoading = loading || groupChannelsQuery?.isLoading;
    return (
        <>
            <StyledDialog open={open} onClose={handleClose} data-target-id="search-contacts-modal" fullscreenOnMobile>
                <StyledDialogTitle onClose={handleClose}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Typography data-target-id="search-contacts-title" variant="h3">
                            {title}
                        </Typography>
                    </Box>
                </StyledDialogTitle>
                <StyledDialogContent>
                    {topArea}
                    {/* Formik is used here to avoid errors using useField in children components */}
                    <Formik
                        initialValues={{
                            query: '',
                        }}
                        onSubmit={console.info}
                    >
                        <TextField
                            data-target-id="search-contacts-textfield"
                            name="query"
                            placeholder={t('common:search')}
                            InputProps={{
                                startAdornment: <Icon name="Search" color={colours.primary.O60} />,
                            }}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setAllGroupChannelsDataLoaded(false);
                            }}
                        />
                    </Formik>
                    {renderGroupConversationButton && (
                        <Button
                            style={{
                                padding: '0.75rem 1.75rem 0.75rem 1rem',
                                marginBottom: '0.5rem',
                                backgroundColor: colours.white.main,
                                color: colours.primary.main,
                            }}
                            size="large"
                            startIcon={
                                <Box width="3rem" display="flex" justifyContent="center">
                                    <Icon name="Network" color={colours.primary.main} />
                                </Box>
                            }
                            endIcon={<Icon size="0.7rem" name="ChevronRight" color={colours.primary.main} />}
                            onClick={openCreateOrAddMembersGroupConversationModal}
                            data-target-id="create-group-conversation-button"
                            fullWidth
                        >
                            <Box mr="auto" ml="0.2rem">
                                {t('common:createGroupConversation')}
                            </Box>
                        </Button>
                    )}
                    <ScrollContainer onScroll={handleScroll}>
                        {groupChannels.map((groupChannel) => (
                            <UserWrapper key={groupChannel.url} $selected={groupChannel === selectedRecipient}>
                                <SimpleHorizontalGroupChannelCard
                                    groupChannel={groupChannel}
                                    padding="0.75rem 1rem"
                                    onClick={() => setSelectedRecipient(groupChannel)}
                                />
                            </UserWrapper>
                        ))}
                        {users.map((user) => (
                            <UserWrapper key={user?.id} $selected={user === selectedRecipient}>
                                <SimpleHorizontalUserCard
                                    user={user as User}
                                    padding="0.75rem 1rem"
                                    onClick={() => setSelectedRecipient(user as User)}
                                />
                            </UserWrapper>
                        ))}
                        {isLoading && <Loader />}
                    </ScrollContainer>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledBoxButtonContainer>
                        <Button
                            data-target-id="search-contacts-cancel-button"
                            variant="outlined"
                            onClick={handleClose}
                            style={{ flex: '0 0 calc(50% - 0.25rem)', marginRight: '0.5rem' }}
                        >
                            {t('common:cancel')}
                        </Button>
                        <Button
                            data-target-id="search-contacts-submit-button"
                            onClick={() => {
                                if (selectedRecipient) {
                                    logEventDynamic(AnalyticsEvent.ShareViaPrivateMessageConfirmation);
                                    onSelection(selectedRecipient);
                                    handleClose();
                                }
                            }}
                            disabled={!selectedRecipient}
                            style={{ flex: '0 0 calc(50% - 0.25rem)', marginLeft: '0.5rem' }}
                        >
                            {submitLabel}
                        </Button>
                    </StyledBoxButtonContainer>
                </StyledDialogActions>
            </StyledDialog>

            {createOrAddMembersGroupConversationModalProps.open && (
                <CreateOrAddMembersGroupConversationModal
                    onSelection={onCreateOrAddMembersGroupConversationSelection}
                    {...createOrAddMembersGroupConversationModalProps}
                />
            )}
        </>
    );
};

export default SearchAllUsersAndGroupsModal;
